import { useEffect, useState } from "react";
import { DeviceMobile, X } from "@phosphor-icons/react";

/**
 * InstallPrompt
 * - Listens for the `beforeinstallprompt` event (Chrome/Edge/Android/Brave/Samsung Internet)
 * - For iOS Safari (no event fires) shows a tiny "Add to Home Screen" hint on first visit
 * - User can dismiss; we remember the choice in localStorage for 30 days
 *
 * Placement: rendered globally inside <App> so it appears across the app once.
 */
const DISMISS_KEY = "rf_pwa_dismissed_until";
const INSTALLED_KEY = "rf_pwa_installed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIosSafari() {
  const ua = window.navigator.userAgent || "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIos && isSafari;
}

export default function InstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) return;
      if (localStorage.getItem(INSTALLED_KEY) === "1") return;
    } catch {}

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setEvt(e);
    };
    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, "1"); } catch {}
      setEvt(null);
      setShowIos(false);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    if (isIosSafari()) {
      const t = setTimeout(() => setShowIos(true), 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
        clearTimeout(t);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      // Snooze for 30 days
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 30 * 24 * 60 * 60 * 1000));
    } catch {}
    setEvt(null);
    setShowIos(false);
  };

  const install = async () => {
    if (!evt) return;
    try {
      evt.prompt();
      const choice = await evt.userChoice;
      if (choice?.outcome === "accepted") {
        try { localStorage.setItem(INSTALLED_KEY, "1"); } catch {}
      }
    } catch {}
    setEvt(null);
  };

  if (!evt && !showIos) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-[60] animate-in slide-in-from-bottom-4 fade-in duration-300"
      role="dialog"
      aria-label="Install RecipeFiler"
    >
      <div className="bg-rf-cream border-2 border-rf-amber/40 rounded-2xl shadow-2xl p-4 sm:p-5 flex items-start gap-3">
        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-rf-amber/25 flex items-center justify-center text-rf-orange">
          <DeviceMobile size={26} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-extrabold text-rf-ink text-base leading-tight">
            Add RecipeFiler to your Home Screen
          </p>
          <p className="text-xs sm:text-sm text-rf-ink-soft mt-1 leading-snug">
            {showIos
              ? <>Tap <b className="font-mono">Share</b> in Safari, then <b>Add to Home Screen</b> — one-tap access next time.</>
              : "One-tap launch — perfect when your hands are floury at the hob."}
          </p>
          {evt && (
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="btn-amber !py-2 !px-4 !text-xs mt-3"
            >
              Install app
            </button>
          )}
        </div>
        <button
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="flex-shrink-0 w-8 h-8 rounded-full bg-rf-cream-alt hover:bg-rf-amber/40 flex items-center justify-center text-rf-ink-soft"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
